<template>
    <div class="tw:mt-4 tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:p-5">
        <!-- Header -->
        <div class="tw:flex tw:items-center tw:gap-2 tw:mb-4">
            <svg class="tw:w-5 tw:h-5 tw:text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <h4 class="tw:text-sm tw:font-bold tw:text-slate-800 tw:mb-0">Calculadora de cambio</h4>
        </div>

        <div class="tw:grid tw:grid-cols-1 md:tw:grid-cols-3 tw:gap-4 tw:items-end">
            <!-- Importe a pagar -->
            <div>
                <label class="tw:block tw:text-xs tw:font-medium tw:text-slate-500 tw:mb-1">
                    Importe a pagar
                </label>
                <div class="tw:relative">
                    <input
                        type="text"
                        :value="formatPrecio(amountDue)"
                        disabled
                        class="tw:w-full tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:px-3 tw:py-2.5 tw:text-sm tw:text-slate-500 tw:cursor-not-allowed"
                    />
                </div>
            </div>

            <!-- Importe recibido -->
            <div>
                <label class="tw:block tw:text-xs tw:font-medium tw:text-slate-500 tw:mb-1">
                    Importe recibido
                </label>
                <div class="tw:relative">
                    <input
                        type="number"
                        step="0.01"
                        :value="localAmount"
                        @input="onAmountInput"
                        placeholder="0,00"
                        data-cy="change-calculator-amount"
                        class="tw:w-full tw:rounded-lg tw:border tw:px-3 tw:py-2.5 tw:text-sm tw:outline-none tw:transition-colors"
                        :class="localAmount >= amountDue
                            ? 'tw:border-green-300 tw:focus:tw:border-green-500 tw:ring-1 tw:ring-green-200'
                            : 'tw:border-slate-300 tw:focus:tw:border-primary tw:ring-1 tw:ring-primary/20'"
                    />
                    <span class="tw:absolute tw:right-3 tw:top-1/2 -tw:translate-y-1/2 tw:text-xs tw:text-slate-400">€</span>
                </div>
            </div>

            <!-- Cambio -->
            <div>
                <label class="tw:block tw:text-xs tw:font-medium tw:text-slate-500 tw:mb-1">
                    Cambio
                </label>
                <div
                    class="tw:rounded-lg tw:px-3 tw:py-2.5 tw:text-sm tw:font-bold tw:text-center tw:transition-colors tw:min-h-[42px] tw:flex tw:items-center tw:justify-center"
                    :class="changeClass"
                >
                    <template v-if="localAmount === 0">
                        <span class="tw:text-slate-400 tw:font-normal">—</span>
                    </template>
                    <template v-else-if="localAmount >= amountDue">
                        <span class="tw:flex tw:items-center tw:gap-1.5">
                            <svg class="tw:w-4 tw:h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            {{ formatPrecio(change) }}
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw:flex tw:items-center tw:gap-1.5 tw:text-red-600">
                            <svg class="tw:w-4 tw:h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
                            </svg>
                            Importe insuficiente
                        </span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import filters from '@/filters';

    const props = defineProps({
        amountDue: { type: Number, required: true },
        modelValue: { type: [Number, String], default: 0 },
    });

    const emit = defineEmits(['amountChanged']);

    const formatPrecio = filters.formatear_precio;

    // ── Ref local para reactividad inmediata del template ──
    const localAmount = ref(Number(props.modelValue) || 0);

    // Sincroniza prop → local SOLO cuando el padre cambia el valor externamente
    // (no en cada emit, para evitar round-trip que congela el input)
    watch(() => props.modelValue, val => {
        const numVal = Number(val) || 0;
        if (numVal !== localAmount.value) {
            localAmount.value = numVal;
        }
    });

    function onAmountInput(e) {
        const val = e.target.valueAsNumber;
        const numVal = isNaN(val) ? 0 : val;
        localAmount.value = numVal;
        emit('amountChanged', numVal);
    }

    const change = computed(() => {
        return Math.max(0, localAmount.value - (props.amountDue || 0));
    });

    const changeClass = computed(() => {
        if (localAmount.value === 0) return 'tw:bg-slate-50 tw:text-slate-400';
        if (localAmount.value >= (props.amountDue || 0)) return 'tw:bg-green-50 tw:text-green-700';
        return 'tw:bg-red-50 tw:text-red-600';
    });
</script>
